import { Box, Icon, Typography } from '@mui/material'
import {
  addMonths,
  eachDayOfInterval,
  format,
  isBefore,
  isSameDay,
  isToday,
  setYear,
  subMonths,
} from 'date-fns'
import React, { useEffect, useState } from 'react'
import useDates from 'services/hooks/useDate'

const DayDiv = ({ onClick, selectedDate, dateInterval, day }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        height: '35px',
        width: 'calc(100% / 7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '8px',
        cursor: 'pointer',
        color:
          isSameDay(selectedDate.start, day.object) || isSameDay(selectedDate.end, day.object)
            ? 'white.main'
            : isToday(day.object)
            ? 'alternate.main'
            : '',
        backgroundColor:
          isSameDay(selectedDate.start, day.object) || isSameDay(selectedDate.end, day.object)
            ? 'alternate.main'
            : dateInterval?.some((date) => date.getTime() === day.object.getTime())
            ? 'alternate.light'
            : '',
        borderRadius:
          isSameDay(selectedDate.start, day.object) || isSameDay(selectedDate.end, day.object)
            ? '3.5px'
            : '',
      }}
    >
      {day.day.toString().padStart(2, '0')}
    </Box>
  )
}

const AdxDateRangePicker = ({ selectedYear, selectedDate, setSelectedDate, customWidth }) => {
  const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const [calendarValue, setCalendarValue] = useState(new Date())
  const [dateInterval, setDateInterval] = useState([])

  const { currentMonth, nextMonth } = useDates({ date: calendarValue })

  const monthName = {
    current: currentMonth[0]?.object !== undefined && format(currentMonth[0]?.object, 'MMMM yyyy'),
    next: nextMonth[0]?.object !== undefined && format(nextMonth[0]?.object, 'MMMM yyyy'),
  }

  const handleSelectDate = (date) => {
    if (!selectedDate.start || selectedDate.end) {
      setSelectedDate({ start: date, end: null })
    } else {
      setSelectedDate((prev) => ({
        start: isBefore(date, prev.start) ? date : prev.start,
        end: isBefore(date, prev.start) ? prev.start : date,
      }))
    }
  }

  const handleDateInterval = () => {
    let dayOfInterval

    if (selectedDate.start && selectedDate.end) {
      dayOfInterval = eachDayOfInterval({
        start: selectedDate.start,
        end: selectedDate.end,
      })
    }

    const filteredDate = dayOfInterval?.filter(
      (date) => !isSameDay(date, selectedDate.start) && !isSameDay(date, selectedDate.end)
    )

    setDateInterval(filteredDate)
  }

  useEffect(() => {
    setCalendarValue(setYear(calendarValue, selectedYear))
  }, [selectedYear])

  useEffect(() => {
    handleDateInterval()
  }, [selectedDate.start, selectedDate.end])

  return (
    <Box
      sx={{
        width: customWidth ? customWidth : '100%',
        display: 'flex',
        gap: '32px',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            onClick={() => setCalendarValue(subMonths(calendarValue, 1))}
            sx={{
              width: '35px',
              height: '35px',
              borderRadius: '3.5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #8A8A8A',
              color: 'light-gray-text.main',
              cursor: 'pointer',
            }}
          >
            <Icon>chevron_left</Icon>
          </Box>
          <Typography variant="h4" fontWeight="bold">
            {monthName.current}
          </Typography>
          <Box
            sx={{
              width: '35px',
              height: '35px',
              borderRadius: '3.5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #8A8A8A',
              color: 'light-gray-text.main',
              visibility: 'hidden',
            }}
          >
            <Icon>chevron_right</Icon>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          <Box sx={{ display: 'flex', width: '100%' }}>
            {dayOfWeek.map((day, index) => (
              <Box
                key={index}
                sx={{
                  width: 'calc(100% / 7)',
                  textAlign: 'center',
                }}
              >
                <Typography variant="subtitle2" color="light-gray-text.main">
                  {day}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {currentMonth.map((day, index) => {
              if (index === 0) {
                return (
                  <React.Fragment key={index}>
                    {Array.from({ length: day.hari }, (_, k) => (
                      <Box
                        key={k}
                        sx={{ width: 'calc(100% / 7)', height: '35px', marginTop: '8px' }}
                      />
                    ))}
                    <DayDiv
                      onClick={() => handleSelectDate(day.object)}
                      selectedDate={selectedDate}
                      dateInterval={dateInterval}
                      day={day}
                    />
                  </React.Fragment>
                )
              }

              return (
                <React.Fragment key={index}>
                  <DayDiv
                    onClick={() => handleSelectDate(day.object)}
                    selectedDate={selectedDate}
                    dateInterval={dateInterval}
                    day={day}
                  />
                </React.Fragment>
              )
            })}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              width: '35px',
              height: '35px',
              borderRadius: '3.5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #8A8A8A',
              color: 'light-gray-text.main',
              visibility: 'hidden',
            }}
          >
            <Icon>chevron_left</Icon>
          </Box>
          <Typography variant="h4" fontWeight="bold">
            {monthName.next}
          </Typography>
          <Box
            onClick={() => setCalendarValue(addMonths(calendarValue, 1))}
            sx={{
              width: '35px',
              height: '35px',
              borderRadius: '3.5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #8A8A8A',
              color: 'light-gray-text.main',
              cursor: 'pointer',
            }}
          >
            <Icon>chevron_right</Icon>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          <Box sx={{ display: 'flex', width: '100%' }}>
            {dayOfWeek.map((day, index) => (
              <Box
                key={index}
                sx={{
                  width: 'calc(100% / 7)',
                  textAlign: 'center',
                }}
              >
                <Typography variant="subtitle2" color="light-gray-text.main">
                  {day}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {nextMonth.map((day, index) => {
              if (index === 0) {
                return (
                  <React.Fragment key={index}>
                    {Array.from({ length: day.hari }, (_, k) => (
                      <Box
                        key={k}
                        sx={{ width: 'calc(100% / 7)', height: '35px', marginTop: '8px' }}
                      />
                    ))}
                    <DayDiv
                      onClick={() => handleSelectDate(day.object)}
                      selectedDate={selectedDate}
                      dateInterval={dateInterval}
                      day={day}
                    />
                  </React.Fragment>
                )
              }

              return (
                <React.Fragment key={index}>
                  <DayDiv
                    onClick={() => handleSelectDate(day.object)}
                    selectedDate={selectedDate}
                    dateInterval={dateInterval}
                    day={day}
                  />
                </React.Fragment>
              )
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default AdxDateRangePicker
